// src/pages/Home.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import HeroSection from "../components/HeroSection";
import CounterSection from "../components/CounterSection";
import "./Home.css";

const Home = () => {
  const solutions = [
    {
      title: "Data Intelligence & Insights",
      description:
        "High-quality, targeted data solutions to help you reach the right audience and make informed decisions.",
    },
    {
      title: "Digital Marketing & Lead Generation",
      description:
        "Strategic campaigns designed to increase visibility, engagement, and qualified leads.",
    },
    {
      title: "Business Growth Consulting",
      description:
        "Expert guidance to help you identify opportunities, optimize performance, and scale effectively.",
    },
    {
      title: "Automation & Technology Solutions",
      description:
        "Smart tools and systems that streamline operations and improve efficiency.",
    },
    {
      title: "Custom Solutions",
      description:
        "Tailored services built specifically around your business needs and goals.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Seagull Partners | Official B2B Data &amp; Marketing Company</title>
        <meta
          name="description"
          content="Seagull Partners is the official B2B data and marketing company delivering verified lists, lead generation, and growth services for teams worldwide today."
        />
        <link rel="canonical" href="https://www.seagullpartner.com/" />

        <meta
          property="og:title"
          content="Seagull Partners | Official B2B Data &amp; Marketing Company"
        />
        <meta
          property="og:description"
          content="Seagull Partners is the official B2B data and marketing company delivering verified lists, lead generation, and growth services for teams worldwide today."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.seagullpartner.com/" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Seagull Partners",
            url: "https://www.seagullpartner.com/",
            logo: "https://www.seagullpartner.com/favicon.ico",
          })}
        </script>
      </Helmet>

      <HeroSection />

      {/* Add CounterSection right after HeroSection */}
      <CounterSection style={{ padding: '40px 0', margin: 0 }} />

      <main>
        {/* Who We Are Section with Background Image */}
        <section className="section who-we-are-section">
          <div className="background-overlay"></div>
          <div className="container">
            <h2 className="section-title">Who We Are</h2>
            <div className="content-wrapper">
              <div className="floating-content">
                <p>
                  Seagull Partners is a growth-focused B2B solutions company
                  helping businesses make smarter, data-driven decisions and achieve measurable business results.
                  We combine data intelligence, B2B marketing expertise, 
                  digital marketing solutions, and advanced technology to deliver scalable business growth solutions, 
                  including lead generation and B2B data support, at every stage of your journey.
                </p>
                <p>
                  Our approach is simple: understand your challenges, design the
                  right solution, and help you grow with confidence.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Solutions Section with Background Image */}
        <section className="section solutions-section">
          <div className="background-overlay"></div>
          <div className="container">
            <h2 className="section-title">Our Solutions</h2>
            <p className="section-subtitle">
              We deliver flexible, customized solutions designed to support
              sales, marketing, and business growth.
            </p>

            <div className="steps-container">
              <div className="step floating-card">
                <div className="step-number">1</div>
                <h3>Data Intelligence & Insights</h3>
                <p>
                  High-quality, targeted data solutions to help you reach the
                  right audience and make informed decisions.
                </p>
              </div>
              <div className="step floating-card">
                <div className="step-number">2</div>
                <h3>Digital Marketing & Lead Generation</h3>
                <p>
                  Strategic campaigns designed to increase visibility,
                  engagement, and qualified leads.
                </p>
              </div>
              <div className="step floating-card">
                <div className="step-number">3</div>
                <h3>Business Growth Consulting</h3>
                <p>
                  Expert guidance to help you identify opportunities, optimize
                  performance, and scale effectively.
                </p>
              </div>
              <div className="step floating-card">
                <div className="step-number">4</div>
                <h3>Automation & Technology Solutions</h3>
                <p>
                  Smart tools and systems that streamline operations and improve
                  efficiency.
                </p>
              </div>
              <div className="step floating-card">
                <div className="step-number">5</div>
                <h3>Custom Solutions</h3>
                <p>
                  Tailored services built specifically around your business
                  needs and goals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How We Work Section with Background Image */}
        <section className="section how-we-work-section">
          <div className="background-overlay"></div>
          <div className="container">
            <h2 className="section-title">How We Work</h2>
            <div className="steps-container">
              <div className="step floating-card">
                <div className="step-number">1</div>
                <h3>Understand</h3>
                <p>
                  We take time to understand your business, market, and growth
                  objectives.
                </p>
              </div>
              <div className="step floating-card">
                <div className="step-number">2</div>
                <h3>Build</h3>
                <p>
                  We design customized strategies and solutions aligned with
                  your goals.
                </p>
              </div>
              <div className="step floating-card">
                <div className="step-number">3</div>
                <h3>Grow</h3>
                <p>
                  We support execution, optimization, and continuous improvement
                  to deliver real results.
                </p>
              </div>
            </div>
          </div>
        </section>

{/* Animated Tagline */}
<section className="animated-tagline-section">
  <div className="container">
    <div className="tagline-wrapper">
      <h2 className="sliding-tagline">
        {["Seagull", "Partners:", "See", "the", "Lead", "Secure", "the", "Profit"].map((word, index) => (
          <span 
            key={index} 
            className={`tagline-word word-${index}`}
            style={{animationDelay: `${index * 0.2}s`}}
          >
            {word}
          </span>
        ))}
      </h2>
    </div>
  </div>
</section>

        {/* CTA Section
        <section className="section cta-section">
          <div className="container">
            <h2 className="cta-title">
              Ready to Think Smarter and Rise Faster?
            </h2>
            <p className="cta-text">
              Let's talk about your goals and explore how Seagull can help you
              grow with confidence.
            </p>
            <a href="/contact" className="btn cta-btn">
              📩 Contact Us Today
            </a>
          </div>
        </section> */}
      </main>
    </>
  );
};

export default Home;
